import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { useAnalyticsView } from '@/features/amplitude'
import { getActiveTaxEstimateWithinActiveQuarter } from '@/features/taxes/quarterly/userTaxEstimates.selector'
import { fetchUserTaxEstimates } from '@/features/taxes/quarterly/userTaxEstimates.slice'
import { useReselector } from '@/utils/sharedHooks'
import { EstimateBreakdownV2 } from './EstimateBreakdownV2'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  BreakdownVersion,
  selectCalculatorVersionByTaxQuarter,
} from '@/selectors/userTaxCalculation.selectors'
import { DefaultBreakdown } from './DefaultBreakdown'
import { EstimateBreakdownV3Wrapper } from './EstimateBreakdownV3Wrapper'
import { StateBreakdownWrapper } from './StateBreakdownWrapper'
import { useBreakdownTransparency } from './useBreakdownTransparency'
import { Loader } from '@/ui'

import { fetchAllQuarterlyTaxEstimateDetailsIfNeeded } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import { fetchUserTaxCalculationsIfNeeded } from '@/features/admin/user-tax-calculations/userTaxCalculation.slice'

export const EstimateBreakdown = () => {
  const { taxQuarter } = useParams()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)

  const dispatch = useAppDispatch()
  const pageView = useAnalyticsView()

  const estimateVersion = useReselector(
    selectCalculatorVersionByTaxQuarter,
    taxQuarter
  )
  const estimates = useReselector(getActiveTaxEstimateWithinActiveQuarter)
  const estimate = estimates.find((e) => e.filingState)
  const transparencyEnabled = useBreakdownTransparency()

  useEffect(() => {
    async function batchFetch() {
      setLoading(true)
      await Promise.all([
        dispatch(fetchUserTaxEstimates()),
        dispatch(fetchAllQuarterlyTaxEstimateDetailsIfNeeded()),
        dispatch(fetchUserTaxCalculationsIfNeeded(true)),
      ])
      setLoading(false)
    }
    batchFetch()
  }, [dispatch])

  useEffect(() => {
    pageView('qte calc breakdown', {
      qte_quarter: estimate?.taxQuarter || 'undefined',
      qte_states:
        searchParams.get('state') || estimate?.filingState || 'undefined',
    })
  }, [pageView, estimate?.taxQuarter, estimate?.filingState, searchParams])

  const stateParam = searchParams.get('state') ?? undefined
  const isStateSection = searchParams.get('section') === 'state'

  if (loading) {
    return <Loader />
  } else if (isStateSection) {
    // A version3 calculation has the output the state page renders from;
    // anything else (ops-entered, older quarters) keeps the generic page.
    return transparencyEnabled &&
      estimateVersion === BreakdownVersion.version3 ? (
      <StateBreakdownWrapper filingState={stateParam} />
    ) : (
      <DefaultBreakdown filingState={stateParam} />
    )
  } else if (estimateVersion === BreakdownVersion.defaultBreakdown) {
    return <DefaultBreakdown filingState={stateParam} />
  } else if (estimateVersion === BreakdownVersion.version2) {
    return <EstimateBreakdownV2 />
  }

  return <EstimateBreakdownV3Wrapper />
}
